import * as React from 'react';
import styled from 'styled-components';
import gettext from '@sdk/lang/gettext';
import Row from '@sdk/components/ui/spacing/Row';
import Text14 from '@sdk/components/ui/typography/Text14';
import TextField from '@sdk/components/forms/TextField';
import useAuth from '@sdk/authentication/useAuth';
import validateNumber, {
  ValidateOptions,
  ValidNumberOption,
} from '@sdk/utils/validation/validateNumber';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import type { UpdateLaborCostSettings } from '@sdk/labor/types';

import CollapsibleSettingsSection from '../CollapsibleSettingsSection';
import RowSpaceBetween from '../RowSpaceBetween';
import SettingsSwitcherRow from '../SettingsSwitcherRow';
import { FIELD_WIDTH_100 } from '../../constants';
import { useSettingsErrorsContext } from '../../SettingsErrorContext';
import { TabType } from '../../types';

const StyledTextField = styled(TextField)`
  max-width: ${FIELD_WIDTH_100}px;
`;

const StyledRow = styled(Row)`
  align-items: center;
`;

type Props = {
  onLaborCostSettingsChange: UpdateLaborCostSettings;
  holidaysEnabled: boolean;
  holidayRateMultiplier: number | string;
};

const HolidayPay: React.FC<React.PropsWithChildren<Props>> = ({
  onLaborCostSettingsChange,
  holidaysEnabled,
  holidayRateMultiplier,
}) => {
  const { toggleError } = useSettingsErrorsContext();
  const { currentUser } = useAuth();

  const validateRateMultiplier = (value: number | string): boolean => {
    return validateNumber(value, {
      [ValidateOptions.validNumber]: ValidNumberOption.nonNegative,
    });
  };
  return (
    <CollapsibleSettingsSection
      withSeparator
      isActive={holidaysEnabled}
      headerItem={
        <SettingsSwitcherRow
          subtitle={gettext('Set a higher pay rate for shifts worked on holidays.')}
          value={holidaysEnabled}
          onChange={(value: boolean): void => onLaborCostSettingsChange({ holidaysEnabled: value })}
          disabled={!currentUser.isAdmin}
        />
      }
      contentItem={
        <RowSpaceBetween>
          <Text14 themeColor={ThemeColorType.gray} semiBold>
            {gettext('Holiday rate ')}
          </Text14>

          <StyledRow>
            <Text14 themeColor={ThemeColorType.gray} pRight={10} pLeft={10} bold>
              {gettext('@ RATE X')}
            </Text14>

            <StyledTextField
              skipErrorMessage
              disabled={!currentUser.isAdmin}
              value={holidayRateMultiplier}
              onChange={(event): void => {
                onLaborCostSettingsChange({
                  holidayRateMultiplier: event.currentTarget.value,
                });
                toggleError(
                  TabType.laborCost,
                  'holidayRateMultiplier',
                  !validateRateMultiplier(event.currentTarget.value)
                );
              }}
              invalid={!validateRateMultiplier(holidayRateMultiplier)}
            />
          </StyledRow>
        </RowSpaceBetween>
      }
    />
  );
};

export default HolidayPay;
